import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkQRGeneration = ({ allUsers, onBulkGenerated }) => {
  const { currentTenant } = useAuth();
  const [selectedRole, setSelectedRole] = useState('all');
  const [qrType, setQrType] = useState('access');
  const [expiryHours, setExpiryHours] = useState('never');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleBulkGenerate = async () => {
    const usersToGenerate = selectedRole === 'all' 
      ? allUsers 
      : allUsers?.filter(u => u?.role === selectedRole);

    if (usersToGenerate?.length === 0) {
      setError('No users selected for bulk generation');
      return;
    }

    if (!confirm(`Generate QR codes for ${usersToGenerate?.length} users?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setProgress({ current: 0, total: usersToGenerate?.length });

    try {
      let expiresAt = null;
      if (expiryHours !== 'never') {
        const expiryDate = new Date();
        expiryDate?.setHours(expiryDate?.getHours() + parseInt(expiryHours));
        expiresAt = expiryDate?.toISOString();
      }

      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < usersToGenerate?.length; i++) {
        const user = usersToGenerate?.[i];
        setProgress({ current: i + 1, total: usersToGenerate?.length });

        try {
          const qrData = `${qrType?.toUpperCase()}-${currentTenant?.id?.substring(0, 8)}-${user?.user_id?.substring(0, 8)}-${Date.now()}-${i}`;
          const isStaff = ['gym_owner', 'staff', 'trainer']?.includes(user?.role);

          if (isStaff && qrType === 'attendance') {
            await supabase?.from('staff_qr_codes')?.insert({
                tenant_id: currentTenant?.id,
                user_id: user?.user_id,
                qr_code_data: qrData,
                is_active: true,
                expires_at: expiresAt,
                generated_at: new Date()?.toISOString()
              });
          } else {
            await supabase?.from('member_barcodes')?.insert({
                tenant_id: currentTenant?.id,
                user_id: user?.user_id,
                barcode_data: qrData,
                barcode_type: 'qr_code',
                is_active: true,
                expires_at: expiresAt,
                generated_at: new Date()?.toISOString()
              });
          }

          successCount++;
        } catch (err) {
          console.error(`Failed to generate QR for user ${user?.user_id}:`, err);
          failCount++;
        }
      }

      setSuccess(`Successfully generated ${successCount} QR codes. ${failCount > 0 ? `${failCount} failed.` : ''}`);
      onBulkGenerated?.();
    } catch (error) {
      console.error('Bulk generation error:', error);
      setError('Failed to complete bulk generation');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const usersCount = selectedRole === 'all' 
    ? allUsers?.length 
    : allUsers?.filter(u => u?.role === selectedRole)?.length;

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Layers" size={24} className="text-primary" />
        <h2 className="text-xl font-bold text-foreground">Bulk QR Code Generation</h2>
      </div>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Select User Group
          </label>
          <Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e?.target?.value)}
          >
            <option value="all">All Users ({allUsers?.length})</option>
            <option value="gym_owner">Gym Owners ({allUsers?.filter(u => u?.role === 'gym_owner')?.length})</option>
            <option value="staff">Staff ({allUsers?.filter(u => u?.role === 'staff')?.length})</option>
            <option value="trainer">Trainers ({allUsers?.filter(u => u?.role === 'trainer')?.length})</option>
            <option value="member">Members ({allUsers?.filter(u => u?.role === 'member')?.length})</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            QR Code Type
          </label>
          <Select
            value={qrType}
            onChange={(e) => setQrType(e?.target?.value)}
          >
            <option value="access">Facility Access</option>
            <option value="attendance">Attendance Tracking</option>
            <option value="profile">Profile Sharing</option>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Expiration
          </label>
          <Select
            value={expiryHours}
            onChange={(e) => setExpiryHours(e?.target?.value)}
          >
            <option value="24">24 Hours</option>
            <option value="168">1 Week</option>
            <option value="720">1 Month</option>
            <option value="never">Never Expires</option>
          </Select>
        </div>

        {progress && (
          <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">Generating QR Codes...</span>
              <span className="text-sm font-bold text-blue-500">
                {progress?.current} / {progress?.total}
              </span>
            </div>
            <div className="w-full bg-background rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all"
                style={{ width: `${(progress?.current / progress?.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-sm text-green-500">{success}</p>
          </div>
        )}

        <div className="p-4 bg-background rounded-lg border border-border">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="text-blue-500 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-foreground mb-1">
                {usersCount} QR codes will be generated
              </p>
              <p className="text-xs text-muted-foreground">
                This will create individual QR codes for each selected user. Existing QR codes will not be affected.
              </p>
            </div>
          </div>
        </div>

        <Button
          onClick={handleBulkGenerate}
          disabled={loading || usersCount === 0}
          className="w-full"
        >
          {loading ? 'Generating...' : `Generate ${usersCount} QR Codes`}
        </Button>
      </div>
    </div>
  );
};

export default BulkQRGeneration;